/********************************/
/*          About vuex          */
/********************************/
// The MIT License (MIT)
//
// Copyright (c) 2015-present Evan You
//
// Permission is hereby granted, free of charge, to any person obtaining a copy
// of this software and associated documentation files (the "Software"), to deal
// in the Software without restriction, including without limitation the rights
// to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
// copies of the Software, and to permit persons to whom the Software is
// furnished to do so, subject to the following conditions:
//
// The above copyright notice and this permission notice shall be included in
// all copies or substantial portions of the Software.
//
// THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
// IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
// FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
// AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
// LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
// OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN
// THE SOFTWARE.

import { mapState, mapActions } from 'vuex';
import individualDiagnose from '../../../../../share/util/individualDiagnose';
import { apiErrorDefaultAction } from '../../../../../share/util/api/errorAction';
import config from '../../../../../share/config';
import customProgress from "../../../../../share/components/vue/CustomProgress";
import diagnoseResource from '../../../../../share/util/diagnose';
import { PHASE, SESSION_PROPERTY } from './constants';
import { standby } from './standby';
import { readHistory } from './readHistory';
import { readHistoryDetail } from './readHistoryDetail';
import { upload } from './upload';
import { terminate } from './terminate';
import { dispose } from './dispose';

export default {
  name: 'requirementMVS2',
  data() {
    return {
      phase: PHASE.START,
      isLoading: false,
      systemId: '', 
      sessionProperty: SESSION_PROPERTY.DEFAULT,
      modal_requirement_MVS2: false,
      confirmChecked: false,
      agreementChecked: false,
      historyList: [],
      // 履歴情報表示画面のヘッダー
      columns: [
        {
          name: 'selected',
          label: `individual.header_preservation_target`,
          useSort: false
        },
        {
          name: "displayName",
          label: `individual.header_store_item`,
          useSort: true
        },
        {
          name: "date",
          label: `individual.header_vehicle_dtc_table_date_time_occurrence`,
          useSort: true
        },
        {
          name: "igon",
          label: `message.header_vehicle_dtc_table_igon_counts`,
          useSort: true
        }
      ],
      currentSort: 'displayName',
      sortDir: config.ASC,
      allSelected: false,
      binFileName: "",
      s3FilePathList: [],
      // プログレスバー
      progress: 0,
      progressMax: 100,
      processedRecord: 0,
      totalRecord: 0,
      exitPromise: null,
    };
  },

  components: {
    'custom-Progress': customProgress,
  },

  props: {
    i18nWsKey: { type: String, default: '', required: true },
    workSupportId: { type: Number, default: '', required: true },
  },

  computed: {
    ...mapState({
      netAppId: state => state.netAppConnector.pairingAppId
    }),

    /**
     * ダイアログのヘッダー色
     */
    headerColor() {
      switch (this.phase) {
        case PHASE.FAILED_READ:
        case PHASE.FAILED_SAVE:
        case PHASE.ERROR:
          return 'danger';
        case PHASE.SUCCESS:
          return 'success';
        case PHASE.START:
          return 'warning';
        case PHASE.HISTORY_LIST:
        case PHASE.EXECUTION:
        default:
          return 'secondary';
      }
    },

    /**
     * ダイアログのヘッダー文言
     */
    headerTitle() {
      // 待機
      const warningTitle = 'message.common_title_warning';
      // 履歴情報表示
      const confirmTitle = 'message.common_title_confirmation';
      // 実行中
      const ExecutionTitle = 'individual.common_title_execution';
      // 停止
      const SuccessTitle = 'message.common_title_success';
      // 失敗表示、エラー、共通エラー
      const ErrorTitle = 'message.common_title_error';
      const titleMap = {
        start: warningTitle,
        history_list: confirmTitle,
        execution: ExecutionTitle,
        success: SuccessTitle,
        failed_read: ErrorTitle,
        failed_save: ErrorTitle,
        error: ErrorTitle
      };
      return this.$t(titleMap[this.phase]);
    },

    /** 表示メッセージ */
    workSupportMessage() {
      const messageMap = {
        start: `${this.i18nWsKey}.label_start`,
        success: `${this.i18nWsKey}.REQ_SP_WS_MVS2_TXT_4`,
        failed_read: `${this.i18nWsKey}.REQ_SP_WS_MVS2_TXT_2`,
        failed_save: `${this.i18nWsKey}.REQ_SP_WS_MVS2_TXT_5`,
        error: `individual.error_communication_error`
      };
      return this.$t(messageMap[this.phase]);
    },

    // 「OK」ボタンの表示確認
    showButtonOk() {
      const showOkButtonMap = {
        success: true,
        failed_read: true,
        failed_save: true,
        error: true
      };
      let result = showOkButtonMap[this.phase];
      if (!result) {
        // 合致しない場合は false とする。
        result = false;
      }
      return result;
    },

    /**
     * [次へ][保存]ボタンの活性・非活性を制御
     * @returns {boolean} trueなら非活性
     */
    isDisableOk() {
      switch (this.phase) {
        case PHASE.START:
          return this.isDisableAgreementConfirm() || this.isLoading;
        case PHASE.HISTORY_LIST:
          return this.hasNoSelectedItems() || this.isLoading;
        default:
          return false;
      }
    },

    /**
     * 表示用配列の作成
     * historyListはインデックスを保持する
     * @returns ソート済み配列
     */
    sortedDisplayList() {
      if (this.sortDir === "") {
        return [...this.historyList];
      }
      const sig = this.sortDir === config.DESC ? -1 : 1;

      return [...this.historyList].sort((a, b) => {
        const aVal = a[this.currentSort];
        const bVal = b[this.currentSort];

        if (aVal === null || aVal === '') return 1;
        if (bVal === null || bVal === '') return -1;

        return aVal < bVal ? -sig : (aVal > bVal ? sig : 0);
      });
    },
    // 診断系のAPIは複数画面から使用されているため
    // 現在の画面で使用されるAPIが診断/データ再生/個別診断/OBDシステムのうちどの機能なのか判別する
    operationScreen() {
      return diagnoseResource.operationFrom(this.$router.currentRoute.value.name);
    },
  },

  methods: {
    ...mapActions('auth', ['logout', 'resetSession']),

    /** 初期化処理 */
    init() {
      this.modal_requirement_MVS2 = true;
      // EUCキーの取得
      this.systemId = individualDiagnose.getCurrentSystemId(this);
      this.sessionProperty = SESSION_PROPERTY.DEFAULT;
    },

    /** Wizardを閉じる */
    async closeWizard() {
      this.$emit('close-wizard');
      this.modal_requirement_MVS2 = false;
    },

    /**
     * API 通信で共通バリデーションでエラーが発生した際のコールバック
     * @param {object} result バリデーション結果
     * @param {string} calledFrom 呼び出し元
     */
    async validateErrorResult(result, calledFrom = '') {
      await apiErrorDefaultAction(
        this,
        result,
        // ログアウト処理のコールバック
        (self) => { self; this.logout(this); },
        // セッションリセット処理のコールバック
        (self) => { self; this.resetSession(this); },
        // その他エラー発生のコールバック
        async (result) => {
          result;
          // terminate,disposeから呼ばれた場合は共通エラーに遷移しない
          if (calledFrom === 'terminate' || calledFrom === 'dispose') return;
          await this.showUnexpectedError();
        },
      );
    },

    /** その他エラー発生時の表示 */
    async showUnexpectedError() {
      // スピナー消えてしまうのを防ぐためPromiseを保持し、各API失敗時に解決を待つ
      this.exitPromise = this.sessionProperty === SESSION_PROPERTY.NON_DEFAULT ? terminate(this) : dispose(this);
      await this.exitPromise;
      // 異常終了：エラー表示
      this.phase = PHASE.ERROR;
    },

    /** 「OK」ボタンの押下処理 */
    onOkClicked() {
      switch (this.phase) {
        case 'success':
        case 'failed_read':
        case 'failed_save':
        case 'error':
          // 機能終了
          this.closeWizard();
          break;
        default:
          break;
      }
    },

    /** 待機画面「次へ」押下 */
    async onNextClicked() {
      // 開始処理
      standby(this);
    },

    /** 保存履歴画面の「終了」押下 */
    async onFinishClicked() {
      // 終了処理
      await terminate(this);
      this.closeWizard();
    },

    /** 保存履歴画面の「全選択」押下 */
    onAllSelectClicked() {
      if (!this.allSelected) {
        // すべての項目のselectedをtrueにする
        this.historyList.forEach(i => {
          i.selected = true;
        });
        this.allSelected = true;
      } else {
        // すべての項目のselectedをfalseにする
        this.historyList.forEach(i => {
          i.selected = false;
        });
        this.allSelected = false;
      }
    },

    /** 保存履歴画面の「保存」押下 */
    async onSaveClicked() {
      //  選択されたレコードを絞り込み
      const selectedItems = this.historyList.filter(item => item.selected === true);
      // 進捗レコード数の初期化
      this.processedRecord = 0;
      this.totalRecord = selectedItems.length;

      // プログレスバー初期化
      this.progress = 0;
      this.progressMax = 100;

      // 履歴データ読出し画面に遷移
      this.phase = PHASE.EXECUTION;

      // 履歴データ読出しAPIの実行
      if (!await readHistory(this)) {
        return;
      }
      // 選択された全ての行(DID)について、ReadHistoryDetailAPIを実行
      if (!await this.readSelectedItems(selectedItems)) {
        return;
      }
      // ファイル保存のAPI実行
      await upload(this);
    },

    /**
     * 選択された全ての行(DID)について、ReadHistoryDetailAPIを実行
     * @param {*} selectedItems 選択されたレコード
     */
    async readSelectedItems(selectedItems) {
      // 選択されたレコード分ループ処理
      for (const item of selectedItems) {
        // 進捗レコード数
        this.processedRecord++;
        // プログレスバー初期化
        this.progress = 0;
        // DIDレコードずつ、ReadHistoryDetailAPIを実行
        if (!await readHistoryDetail(this, item.did)) {
          return false;
        }
      }
      return true;
    },

    /**
     * チェックボックスが2つともチェックされるとfalse(待機)
     * @returns {boolean} trueなら非活性
     */
    isDisableAgreementConfirm() {
      if (!this.confirmChecked || !this.agreementChecked) return true;
      return false;
    },

    /**
     * 1項目でも選択されている場合false(保存履歴表示)
     * @returns {boolean} trueなら非活性
     */
    hasNoSelectedItems() {
      // historyListの中でselectedがtrueの項目を探す
      const foundItem = this.historyList.find(item => item.selected);
      return !foundItem; // 見つからなければtrue、見つかればfalseを返す
    },

    /**
     * sortDirに従ってソートする
     * @param {object} column カラムオブジェクト
     * @returns 
     */
    sort(column) {
      if (!column.useSort) return;
      if (this.currentSort === column.name) {
        this.sortDir = this.sortDir === config.ASC ? config.DESC : config.ASC;
      } else {
        this.currentSort = column.name;
        this.sortDir = config.ASC;
      }
    },

    /**
     * テーブル行に適用するスタイルを指定
     * @param {number} index インデックス
     * @returns 行の背景色
     */
    getTdStyle(index) {
      return { 'table-row-stripe': (index % 2 === 0) && true };
    },

    /**
     * ソートアイコンの作成
     * @returns ソートアイコン
     */
    getSortIcon() {
      if (this.sortDir === config.ASC) {
        return "<span> ▲</span>";
      } else if (this.sortDir === config.DESC) {
        return "<span> ▼</span>";
      } else {
        return "";
      }
    },
  },

  /**
   * DOM作成後実行処理
   */
  async mounted() {
    this.init();
  },
};